import { useEffect, useState } from "react";
import AccessibilityNewOutlinedIcon from "@mui/icons-material/AccessibilityNewOutlined";
import RemoveRedEyeOutlinedIcon from "@mui/icons-material/RemoveRedEyeOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import DirectionsWalkOutlinedIcon from "@mui/icons-material/DirectionsWalkOutlined";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedElement,
  updateSelectedActorState,
} from "../../app/slices/TrainingSlice";
import { ActorState, ActorStateAnimation } from "../../models/ActorState";
import ActorStateLookatDropdown from "./ActorStateLookatDropdown";
import ActorStateOutfitDropdown from "./ActorStateOutfitDropdown";
import useActorStateProperties, {
  ActorStatus,
} from "./ActorStatePropertiesColumn.hooks";
import ActorStatePropertiesHeader from "./ActorStatePropertiesHeader";
import ActorStateMovementToggle from "./ActorStateMovementToggle";
import ActorStatePropInfoField from "./ActorStatePropInfoField";
import ActorStateAnimationSelector from "./ActorStateAnimationSelector";
import ActorPresetEditorField from "../actor_presets/ActorPresetEditorField";
import PiSwitch from "../../components/PiSwitch";
import "../actor_presets/ActorPresetEditor.css";

export default function ActorStatePropertiesEditor(props: {
  layout?: "stack" | "tiles";
}) {
  const { layout = "stack" } = props;
  const selectedActorState = useSelector(selectSelectedElement) as
    | ActorState
    | undefined;
  const { getActorStatus } = useActorStateProperties();
  const [myActorStatus, setMyActorStatus] = useState<ActorStatus>(
    ActorStatus.Invalid
  );
  const dispatch = useDispatch();

  useEffect(() => {
    setMyActorStatus(getActorStatus());
  }, [selectedActorState, getActorStatus]);

  if (!selectedActorState) return null;

  const onChangeField = (field: keyof ActorState, value: any) => {
    dispatch(
      updateSelectedActorState({ ...selectedActorState, [field]: value })
    );
  };

  const onChangeAnimation = (animation: ActorStateAnimation) => {
    dispatch(updateSelectedActorState({ ...selectedActorState, animation }));
  };

  const canSelectMovement =
    (selectedActorState.actorId === "player" &&
      myActorStatus !== ActorStatus.Idle) ||
    (myActorStatus !== ActorStatus.Idle &&
      myActorStatus !== ActorStatus.Spawned);

  if (layout === "tiles") {
    return (
      <div className="viewport3d-actor-editor-grid">
        <ActorPresetEditorField
          name="Identity"
          icon={AccessibilityNewOutlinedIcon}
          className="viewport3d-actor-editor-summary"
          contentClassName="viewport3d-actor-editor-summary-content"
        >
          <div className="viewport3d-actor-editor-stack">
            <ActorStatePropertiesHeader actor={selectedActorState} />
            <ActorStateMovementToggle
              actor={selectedActorState}
              onChange={(movement) => onChangeField("movement", movement)}
              disabled={!canSelectMovement}
              variant="default"
            />
          </div>
        </ActorPresetEditorField>
        <ActorPresetEditorField
          name="Focus And Appearance"
          icon={RemoveRedEyeOutlinedIcon}
          className="viewport3d-actor-editor-behaviour"
        >
          <div className="viewport3d-actor-editor-stack">
            <ActorStateLookatDropdown
              actorState={selectedActorState}
              onChange={(lookat) => onChangeField("lookat", lookat)}
            />
            <ActorStateOutfitDropdown
              actorState={selectedActorState}
              onChange={(outfit) => onChangeField("outfit", outfit)}
              disabled={myActorStatus === ActorStatus.Spawned}
            />
            <PiSwitch
              checked={selectedActorState.resetCharacterParts ?? false}
              label="Reset Character Parts"
              onChange={(checked) =>
                onChangeField("resetCharacterParts", checked)
              }
            />
          </div>
        </ActorPresetEditorField>
        <ActorPresetEditorField
          name="Animation"
          icon={DirectionsWalkOutlinedIcon}
          className="viewport3d-actor-editor-animation"
        >
          <ActorStateAnimationSelector
            actorState={selectedActorState}
            onChange={onChangeAnimation}
            variant="preview"
          />
        </ActorPresetEditorField>
        <ActorPresetEditorField
          name="Attachments"
          icon={Inventory2OutlinedIcon}
          className="viewport3d-actor-editor-attachments"
          contentClassName="pit-creator-scrollbar"
        >
          <ActorStatePropInfoField actorState={selectedActorState} />
        </ActorPresetEditorField>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-[20px] py-[5px] px-[10px]">
      <ActorStatePropertiesHeader actor={selectedActorState} />
      <ActorStateMovementToggle
        actor={selectedActorState}
        onChange={(movement) => onChangeField("movement", movement)}
        disabled={!canSelectMovement}
        variant="compact"
      />
      <ActorStateLookatDropdown
        actorState={selectedActorState}
        onChange={(lookat) => onChangeField("lookat", lookat)}
      />
      <ActorStateAnimationSelector
        actorState={selectedActorState}
        onChange={onChangeAnimation}
      />
      <ActorStateOutfitDropdown
        actorState={selectedActorState}
        onChange={(outfit) => onChangeField("outfit", outfit)}
        disabled={myActorStatus === ActorStatus.Spawned}
      />
      <PiSwitch
        checked={selectedActorState.resetCharacterParts ?? false}
        label="Reset Character Parts"
        onChange={(checked) => onChangeField("resetCharacterParts", checked)}
      />
      <ActorStatePropInfoField
        actorState={selectedActorState}
        variant="compact"
      />
    </div>
  );
}
